import {
  ExclamationTriangleIcon,
  PencilIcon,
  TrashIcon,
  ArrowPathIcon,
  CircleStackIcon,
} from "@heroicons/react/24/outline";
import { createColumnHelper, type ColumnDef } from "@tanstack/react-table";
import { Badge } from "@via/components/badge.tsx";
import { GenericError } from "@via/components/error.tsx";
import IndeterminateCheckbox from "@via/components/inputs/indeterminate-checkbox.tsx";
import InlineInput from "@via/components/inputs/inline-input.tsx";
import {
  MessageBanner,
  BannerType,
  BannerSize,
} from "@via/components/message-banner/message-banner.tsx";
import { Spinner } from "@via/components/loading/loading.tsx";
import Modal from "@via/components/modal.tsx";
import Table, { TableSize } from "@via/components/table/table.tsx";
import {
  useRunAttemptsForManagement,
  useSoftDeleteRunAttempts,
  useRestoreRunAttempts,
  usePurgeRunAttempts,
  useRenameRunAttempt,
  useRemoveRunAttemptTileDb,
  useQueueRunAttemptTileDbIngestion,
} from "@via/pages/projects/runs/runs.api.tsx";
import { getRunStatusLabel } from "@via/pages/projects/runs/utils/helpers.tsx";
import {
  BadgeStatus,
  EMPTY_TABLE_MESSAGE,
  ModalSize,
} from "@via/utils/constants.tsx";
import {
  classNames,
  formatDateString,
  formatFileSize,
} from "@via/utils/helpers.tsx";
import { activeRunStatuses, type ManageableRunAttempt } from "common";
import { useEffect, useState, type JSX } from "react";

const columnHelper = createColumnHelper<ManageableRunAttempt>();

const TRASHED_BADGE = (
  <Badge
    text="Trashed"
    fillColor={BadgeStatus.ErrorFillColor}
    textColor={BadgeStatus.ErrorTextColor}
  />
);

const TILEDB_BADGE = (
  <Badge
    text="In Tracker"
    fillColor={BadgeStatus.SuccessFillColor}
    textColor={BadgeStatus.SuccessTextColor}
  />
);

const TILEDB_QUEUED_BADGE = (
  <Badge
    text="Queued"
    fillColor={BadgeStatus.SuccessFillColor}
    textColor={BadgeStatus.SuccessTextColor}
  />
);

const TILEDB_FAILED_BADGE = (
  <Badge
    text="Failed"
    fillColor={BadgeStatus.ErrorFillColor}
    textColor={BadgeStatus.ErrorTextColor}
  />
);

const TILEDB_REMOVED_BADGE = (
  <Badge
    text="Not Ingested"
    fillColor={BadgeStatus.DefaultFillColor}
    textColor={BadgeStatus.DefaultTextColor}
  />
);

interface ManageRunAttemptsModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  runId: string;
}

const ManageRunAttemptsModal = ({
  open,
  setOpen,
  runId,
}: ManageRunAttemptsModalProps): JSX.Element => {
  const [selectedAttempts, setSelectedAttempts] = useState<
    ManageableRunAttempt[]
  >([]);
  const [showPurgeConfirm, setShowPurgeConfirm] = useState(false);
  const [editableRowId, setEditableRowId] = useState<number | null>(null);

  const {
    data: managementData,
    isLoading,
    isError,
  } = useRunAttemptsForManagement(runId, open);

  const { mutate: softDelete, isPending: isSoftDeletePending } =
    useSoftDeleteRunAttempts();
  const { mutate: restore, isPending: isRestorePending } =
    useRestoreRunAttempts();
  const { mutate: purge, isPending: isPurgePending } = usePurgeRunAttempts();
  const {
    mutate: renameAttempt,
    isPending: isRenamePending,
    isSuccess: isRenameSuccess,
  } = useRenameRunAttempt();
  const { mutate: removeRunAttemptTileDb, isPending: isRemoveTileDbPending } =
    useRemoveRunAttemptTileDb();
  const {
    mutate: queueRunAttemptTileDbIngestion,
    isPending: isQueueTileDbPending,
  } = useQueueRunAttemptTileDbIngestion();

  const isAnyActionPending =
    isSoftDeletePending ||
    isRestorePending ||
    isPurgePending ||
    isRenamePending ||
    isRemoveTileDbPending ||
    isQueueTileDbPending;

  const tileDbEnabled = managementData?.tileDbEnabled ?? false;
  const attempts = managementData?.attempts ?? [];

  const latestAttempt = attempts[0];
  const isLatestRunning =
    latestAttempt &&
    !latestAttempt.deleted &&
    activeRunStatuses.includes(latestAttempt.runStatus as never);

  const isAttemptLocked = (attempt: ManageableRunAttempt): boolean =>
    isLatestRunning && attempt.id === latestAttempt.id;

  const selectableAttempts = attempts.filter(a => !isAttemptLocked(a));

  useEffect(() => {
    setSelectedAttempts([]);
    setShowPurgeConfirm(false);
  }, [managementData]);

  useEffect(() => {
    if (isRenameSuccess) setEditableRowId(null);
  }, [isRenameSuccess]);

  const selectedIds = new Set(selectedAttempts.map(a => a.id));
  const hasActiveSelected = selectedAttempts.some(a => !a.deleted);
  const hasDeletedSelected = selectedAttempts.some(a => a.deleted);
  const selectedAttempt =
    selectedAttempts.length === 1 ? selectedAttempts[0] : null;
  const canRemoveSelectedTileDb =
    tileDbEnabled && selectedAttempt?.sampleTrackerAction === "remove";
  const canQueueSelectedTileDb =
    tileDbEnabled && selectedAttempt?.sampleTrackerAction === "rebuild";

  const allRowsSelected =
    selectableAttempts.length > 0 &&
    selectedAttempts.length === selectableAttempts.length;
  const someRowsSelected = selectedAttempts.length > 0 && !allRowsSelected;

  const toggleAllRowsSelected = (): void => {
    setSelectedAttempts(allRowsSelected ? [] : [...selectableAttempts]);
    setShowPurgeConfirm(false);
  };

  const handleCheckboxToggle = (row: ManageableRunAttempt): void => {
    if (isAttemptLocked(row)) return;
    setSelectedAttempts(prev =>
      prev.some(item => item.id === row.id)
        ? prev.filter(item => item.id !== row.id)
        : [...prev, row]
    );
    setShowPurgeConfirm(false);
  };

  const handleSoftDelete = () => {
    const ids = selectedAttempts.filter(a => !a.deleted).map(a => a.id);
    if (ids.length > 0) softDelete({ runId, attemptIds: ids });
  };

  const handleRestore = () => {
    const ids = selectedAttempts.filter(a => a.deleted).map(a => a.id);
    if (ids.length > 0) restore({ runId, attemptIds: ids });
  };

  const handlePurge = () => {
    if (!showPurgeConfirm) {
      setShowPurgeConfirm(true);
      return;
    }
    const ids = selectedAttempts.map(a => a.id);
    if (ids.length > 0) {
      purge({ runId, attemptIds: ids });
      setShowPurgeConfirm(false);
    }
  };

  const handleRemoveTileDb = () => {
    if (selectedAttempt) {
      removeRunAttemptTileDb({ runId, attemptId: selectedAttempt.id });
    }
  };

  const handleQueueTileDbIngestion = () => {
    if (selectedAttempt) {
      queueRunAttemptTileDbIngestion({
        runId,
        attemptId: selectedAttempt.id,
      });
    }
  };

  const columns = [
    columnHelper.display({
      id: "checkboxColumn",
      header: () => (
        <IndeterminateCheckbox
          checked={allRowsSelected}
          indeterminate={someRowsSelected}
          onChange={toggleAllRowsSelected}
          onClick={e => e.stopPropagation()}
          disabled={isAnyActionPending}
        />
      ),
      cell: ({ row }) => {
        const locked = isAttemptLocked(row.original);
        return (
          <IndeterminateCheckbox
            checked={selectedIds.has(row.original.id)}
            onChange={() => handleCheckboxToggle(row.original)}
            onClick={e => e.stopPropagation()}
            disabled={isAnyActionPending || locked}
            title={
              locked ? "Cannot modify the current running attempt" : undefined
            }
          />
        );
      },
      size: 50,
    }),
    columnHelper.display({
      id: "attempt",
      header: () => <span>Attempt</span>,
      cell: ({ row }) => {
        const index = attempts.indexOf(row.original);
        const displayName =
          row.original.name ?? `Attempt ${attempts.length - index}`;

        if (editableRowId === row.original.id) {
          return (
            <div onClick={e => e.stopPropagation()}>
              <InlineInput
                value={displayName}
                isLoading={isRenamePending}
                onSubmit={name => {
                  renameAttempt({
                    runId,
                    attemptId: row.original.id,
                    name,
                  });
                }}
                onCancel={() => setEditableRowId(null)}
              />
            </div>
          );
        }

        return (
          <div className="flex items-center gap-1.5">
            <span className={row.original.deleted ? "opacity-60" : ""}>
              {displayName}
            </span>
            <button
              type="button"
              onClick={e => {
                e.stopPropagation();
                setEditableRowId(row.original.id);
              }}
              className="text-gray-300 hover:text-indigo-600">
              <PencilIcon className="h-4.5 w-4.5" />
            </button>
          </div>
        );
      },
    }),
    columnHelper.accessor("dateCreated", {
      header: () => <span>Date Created</span>,
      cell: info => (
        <span className={info.row.original.deleted ? "opacity-60" : ""}>
          {formatDateString(info.getValue()?.toString() ?? "", "LLL")}
        </span>
      ),
      enableSorting: false,
    }),
    columnHelper.accessor("runStatus", {
      header: () => <span>Status</span>,
      cell: info => {
        const attempt = info.row.original;
        if (attempt.deleted) return TRASHED_BADGE;
        if (attempt.runStatus) return getRunStatusLabel(attempt.runStatus);
        return "--";
      },
      enableSorting: false,
    }),
    columnHelper.accessor("size", {
      header: () => <span>Size</span>,
      cell: info => (
        <span className={info.row.original.deleted ? "opacity-60" : ""}>
          {formatFileSize(info.getValue()) || "--"}
        </span>
      ),
      enableSorting: false,
    }),
    columnHelper.display({
      id: "tileDb",
      header: () => <span>Sample Tracker</span>,
      cell: ({ row }) => {
        const attempt = row.original;
        switch (attempt.sampleTrackerStatus) {
          case "current":
            return TILEDB_BADGE;
          case "queued":
            return TILEDB_QUEUED_BADGE;
          case "failed":
            return TILEDB_FAILED_BADGE;
          case "not_ingested":
            return TILEDB_REMOVED_BADGE;
          default:
            return <span className="text-xs text-gray-400">--</span>;
        }
      },
      enableSorting: false,
    }),
  ] as Array<ColumnDef<ManageableRunAttempt>>;

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      title="Manage Run Attempts"
      size={ModalSize.XLarge}
      hasCancelButton={true}
      cancelText="Close">
      <div className="flex flex-col gap-4 mb-4">
        <div className="flex items-center justify-between border-b border-gray-200 pb-3">
          <p className="text-sm text-gray-500">
            {selectedAttempts.length > 0
              ? `${selectedAttempts.length} of ${attempts.length} selected`
              : `${attempts.length} attempt${attempts.length !== 1 ? "s" : ""}`}
          </p>
          <div className="flex gap-2">
            {canQueueSelectedTileDb && (
              <button
                type="button"
                disabled={isAnyActionPending}
                onClick={handleQueueTileDbIngestion}
                className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-indigo-50 px-3 py-1.5 text-sm font-medium text-indigo-700 ring-1 ring-inset ring-indigo-600/20 hover:bg-indigo-100 disabled:opacity-50">
                {isQueueTileDbPending ? (
                  <Spinner loading />
                ) : (
                  <ArrowPathIcon className="h-4 w-4" />
                )}
                Rebuild Sample Tracker
              </button>
            )}
            {canRemoveSelectedTileDb && (
              <button
                type="button"
                disabled={isAnyActionPending}
                onClick={handleRemoveTileDb}
                className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-slate-50 px-3 py-1.5 text-sm font-medium text-slate-700 ring-1 ring-inset ring-slate-600/20 hover:bg-slate-100 disabled:opacity-50">
                {isRemoveTileDbPending ? (
                  <Spinner loading />
                ) : (
                  <CircleStackIcon className="h-4 w-4" />
                )}
                Remove From Sample Tracker
              </button>
            )}
            {hasDeletedSelected && (
              <button
                type="button"
                disabled={isAnyActionPending}
                onClick={handleRestore}
                className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-green-50 px-3 py-1.5 text-sm font-medium text-green-700 ring-1 ring-inset ring-green-600/20 hover:bg-green-100 disabled:opacity-50">
                {isRestorePending ? (
                  <Spinner loading />
                ) : (
                  <ArrowPathIcon className="h-4 w-4" />
                )}
                Restore
              </button>
            )}
            {hasActiveSelected && (
              <button
                type="button"
                disabled={isAnyActionPending}
                onClick={handleSoftDelete}
                className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-amber-50 px-3 py-1.5 text-sm font-medium text-amber-700 ring-1 ring-inset ring-amber-600/20 hover:bg-amber-100 disabled:opacity-50">
                {isSoftDeletePending ? (
                  <Spinner loading />
                ) : (
                  <TrashIcon className="h-4 w-4" />
                )}
                Move to Trash
              </button>
            )}
            {selectedAttempts.length > 0 && (
              <button
                type="button"
                disabled={isAnyActionPending}
                onClick={handlePurge}
                className={classNames(
                  "inline-flex items-center gap-1.5 whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium ring-1 ring-inset disabled:opacity-50",
                  showPurgeConfirm
                    ? "bg-red-600 text-white ring-red-600 hover:bg-red-500"
                    : "bg-red-50 text-red-700 ring-red-600/20 hover:bg-red-100"
                )}>
                {isPurgePending ? (
                  <Spinner loading />
                ) : (
                  <ExclamationTriangleIcon className="h-4 w-4" />
                )}
                {showPurgeConfirm
                  ? "Yes, Delete Permanently"
                  : "Delete Permanently"}
              </button>
            )}
          </div>
        </div>

        {showPurgeConfirm && (
          <MessageBanner
            type={BannerType.Warning}
            title="This cannot be undone"
            messages={`${selectedAttempts.length} attempt${selectedAttempts.length !== 1 ? "s" : ""} will be permanently deleted. Click "Yes, Delete Permanently" to proceed.`}
            size={BannerSize.Small}
          />
        )}

        {isError ? (
          <div className="via-generic-error">
            <GenericError message="Failed to load run attempts." />
          </div>
        ) : (
          <Table
            columns={columns}
            data={attempts}
            isLoading={isLoading}
            total={attempts.length}
            pageSize={10}
            size={TableSize.Small}
            emptyMessage={EMPTY_TABLE_MESSAGE.RUN_ATTEMPTS}
            enableSorting={false}
            onRowClick={handleCheckboxToggle}
          />
        )}
      </div>
    </Modal>
  );
};

export default ManageRunAttemptsModal;
